import { CLIENT_STATUS } from '@/constants/http.constants';
import { ERROR_CODES, type ErrorCode, type ValidationIssue } from '@/types/api.types';

interface ApiErrorOptions {
  status: number;
  code: ErrorCode;
  message: string;
  details?: ValidationIssue[];
  requestId?: string;
  cause?: unknown;
}

/**
 * Every failure from the API layer — HTTP error envelope, network failure or timeout —
 * reaches callers as one of these, so UI code has a single error shape to handle.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly details: ValidationIssue[] | undefined;
  readonly requestId: string | undefined;

  constructor(options: ApiErrorOptions) {
    super(options.message, { cause: options.cause });
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.details = options.details;
    this.requestId = options.requestId;
  }

  /** True when retrying cannot help: the request itself was wrong. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  get isNetworkError(): boolean {
    return this.status === CLIENT_STATUS.NETWORK_ERROR || this.status === CLIENT_STATUS.TIMEOUT;
  }

  static network(message: string, cause?: unknown): ApiError {
    return new ApiError({
      status: CLIENT_STATUS.NETWORK_ERROR,
      code: ERROR_CODES.SERVICE_UNAVAILABLE,
      message,
      cause,
    });
  }

  static timeout(timeoutMs: number, cause?: unknown): ApiError {
    return new ApiError({
      status: CLIENT_STATUS.TIMEOUT,
      code: ERROR_CODES.SERVICE_UNAVAILABLE,
      message: `The request timed out after ${timeoutMs}ms`,
      cause,
    });
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
